import './ModelFitButton.scss';
import cn from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { mapeQuality, QUALITY_LABELS } from '../utils/dtc-commerce-mmm-model-fit';

interface ModelFitButtonProps {
  onClick: () => void;
}

export const ModelFitButton = ({ onClick }: ModelFitButtonProps) => {
  const { data } = useDtcCommerceMmmData();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Mirror the right edge of the top-nav KPI/refresh dropdown. The two
  // controls live in different containers (top nav vs sub nav) with different
  // paddings, and the body's scrollbar can affect them differently — so the
  // most robust approach is to measure the dropdown's actual rendered right
  // edge at runtime and set this button's margin-right to match. Depending on
  // `data` ensures the effect re-runs once the dropdown has rendered (both
  // wait on the same data context).
  useLayoutEffect(() => {
    const buttonEl = buttonRef.current;
    if (!buttonEl) return undefined;
    const pageEl = buttonEl.closest('.bpm-page');
    const subheaderEl = buttonEl.closest('.dtc-commerce-mmm-subheader');
    if (!(pageEl instanceof HTMLElement) || !(subheaderEl instanceof HTMLElement)) return undefined;
    const bodyEl = pageEl.querySelector(':scope > .body');
    const dropdownEl = pageEl.querySelector('.dtc-commerce-mmm-run-picker');
    if (!(bodyEl instanceof HTMLElement) || !(dropdownEl instanceof HTMLElement)) return undefined;

    const update = () => {
      // The dropdown's CSS reads --mmm-scrollbar-w to align with the body
      // content right edge. Set it first so the dropdown's own layout is
      // correct before we measure it.
      const sbw = bodyEl.offsetWidth - bodyEl.clientWidth;
      document.documentElement.style.setProperty('--mmm-scrollbar-w', `${sbw}px`);
      const dropdownRect = dropdownEl.getBoundingClientRect();
      const subheaderRect = subheaderEl.getBoundingClientRect();
      const offset = subheaderRect.right - dropdownRect.right;
      document.documentElement.style.setProperty('--mmm-fit-right', `${offset}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(bodyEl);
    ro.observe(dropdownEl);
    ro.observe(subheaderEl);
    return () => ro.disconnect();
  }, [data]);

  if (!data) return undefined;
  const quality = mapeQuality(data.modelInfo.testMape);

  return (
    <Button
      size={Size.LG}
      ref={buttonRef}
      appearance={Appearance.OUTLINED}
      className='dtc-commerce-mmm-model-fit-trigger'
      onClick={onClick}
    >
      <span className={cn('dtc-commerce-mmm-model-fit-dot', quality)} />
      Model Fit: {QUALITY_LABELS[quality]}
    </Button>
  );
};
